import axios from "axios";
import { API_BASE_URL } from "../../config";

/**
 * Employee attendance — marking someone absent for a date range.
 *
 * An absent employee is skipped by lead round-robin for the whole range, and
 * becomes available again by itself once it ends (no un-marking needed).
 *
 * Who may mark whom is hierarchy-based and decided server-side: rows carry
 * `can_mark_attendance`, so the UI never computes the tree.
 */

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("cms_token")}`,
  "Content-Type": "application/json",
});

const apiError = (err, fallback) => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  if (status === 403) {
    return new Error(
      data?.message || "You can only mark attendance for employees who report to you.",
    );
  }
  if (status === 404) return new Error(data?.message || "Employee not found");
  const firstFieldError = data?.errors?.[Object.keys(data?.errors || {})[0]]?.[0];
  return new Error(firstFieldError || data?.message || data?.error || fallback);
};

/**
 * @param {number|string} id employee id
 * @param {{from_date?: string, to_date?: string, reason?: string}} payload
 *        both dates YYYY-MM-DD; omitting them means today only.
 */
export const markAbsent = async (id, payload = {}) => {
  try {
    const res = await axios.post(`${API_BASE_URL}/employees/${id}/mark-absent`, payload, {
      headers: authHeaders(),
    });
    const data = res.data || {};
    if (data.status === false || data.status === 422) {
      throw new Error(data.message || "Could not mark this employee absent");
    }
    return data.data || data;
  } catch (err) {
    if (err instanceof Error && !err.response) throw err;
    throw apiError(err, "Could not mark this employee absent");
  }
};

/** Clears today's absence by default; pass {date} or {absence_id} to be exact. */
export const unmarkAbsent = async (id, payload = {}) => {
  try {
    const res = await axios.post(`${API_BASE_URL}/employees/${id}/unmark-absent`, payload, {
      headers: authHeaders(),
    });
    const data = res.data || {};
    if (data.status === false) throw new Error(data.message || "Could not mark this employee present");
    return data.data || data;
  } catch (err) {
    if (err instanceof Error && !err.response) throw err;
    throw apiError(err, "Could not mark this employee present");
  }
};
